import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight, Layers, Users } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { RaisedToYouCard } from '@/features/OrgLayout/RaisedToYouCard';
import { cn } from '@/lib/cn';
import { useOrgOverview, useOrgInsights, useOrgTrends } from '@/lib/hooks/useOrg';
import { BAND_META, BAND_ORDER } from '@/features/OrgLayout/readiness';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { KpiCard } from '@/features/OrgLayout/primitives/KpiCard';
import { OrgProgressBar } from '@/features/OrgLayout/primitives/OrgProgressBar';
import { Sparkline } from '@/features/OrgLayout/primitives/Sparkline';
import type { ReadinessBand } from '@/lib/api/org';

export const Route = createFileRoute('/org/')({
  component: OverviewPage,
});

const TIER_SUBTITLE: Record<ReadinessBand, string> = {
  product_ready: 'Product / Day-1',
  service_ready: 'Service / Dream',
  developing: 'Developing',
  needs_support: 'Not yet ready',
};

const REASON_LABEL: Record<string, string> = {
  dormant: 'Dormant ≥10d',
  low_attendance: 'Low attendance',
  needs_support: 'Needs support',
  dropped: 'Dropped',
  paused: 'Paused',
  broken_streak: 'Broken streak',
};

// skill-gap cell colour by column (calmer at the "good" end, louder at the "bad" end)
const GAP_COLS = [
  { key: 'strong', label: 'Strong', cls: 'bg-emerald-500' },
  { key: 'ok', label: 'OK', cls: 'bg-lime-500' },
  { key: 'weak', label: 'Weak', cls: 'bg-amber-500' },
  { key: 'critical', label: 'Critical', cls: 'bg-red-500' },
] as const;

function OverviewPage() {
  const overview = useOrgOverview();
  const insights = useOrgInsights();
  const trends = useOrgTrends(); // best-effort: never gates loading/error below
  const navigate = useNavigate();

  if (overview.isLoading || insights.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-14 w-72 rounded-[24px]" />
        <Skeleton className="h-[92px] w-full rounded-[24px]" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[210px] rounded-[24px]" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-[24px]" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (overview.isError || insights.isError || !overview.data || !insights.data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-muted-foreground">Couldn&apos;t load your organization dashboard.</p>
        <Button
          className="mt-4"
          onClick={() => {
            overview.refetch();
            insights.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const o = overview.data;
  const ins = insights.data;
  const ns = ins.north_star;
  const tier = ins.tier_counts;
  const tierTotal =
    tier.product_ready + tier.service_ready + tier.developing + tier.needs_support || 1;
  const trendsData = trends.data; // best-effort: undefined while loading/erroring is fine

  // Students who need support (needs_support readiness tier) — not the number of
  // alert *items*, which is always ≤2 and mislabels the count as students.
  const riskCount = tier.needs_support;

  return (
    <div className="space-y-6">
      {/*
        Above everything except the header. It renders only when a trainer has
        raised something, so it never costs the dashboard a row on a quiet day —
        and when it is there it is the one item on this page that is a to-do
        rather than a number.
      */}
      <RaisedToYouCard />

      {/* HEADER */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[48px] font-bold leading-[60px] text-[#111827]">Dashboard</h1>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-[#6b7280]">
            <span className="flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              {o.active_batches} {o.active_batches === 1 ? 'Batch' : 'Batches'}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {o.total_students} Students
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {o.total_trainers} {o.total_trainers === 1 ? 'Trainer' : 'Trainers'}
            </span>
          </div>
        </div>
      </div>

      {/* TODAY'S FOCUS */}
      <OrgCard className="flex items-center gap-5 px-[25px] py-[21px]">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#fff1ec]">
          <AlertTriangle className="h-[22px] w-[22px] text-[#ff5a1f]" />
        </div>
        <div className="flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[1.1px] text-[#ff5a1f]">
            Today&apos;s Focus
          </p>
          <p className="mt-0.5 text-xl font-bold text-[#111827]">
            {riskCount > 0
              ? `${riskCount} ${riskCount === 1 ? 'student needs' : 'students need'} support`
              : "You're all caught up"}
          </p>
        </div>
        {riskCount > 0 && (
          <Button
            variant="ghost"
            onClick={() => navigate({ to: '/org/readiness' })}
            className="h-auto shrink-0 gap-2 rounded-[16px] border-[1.5px] border-[#ff5a1f] bg-transparent px-[21.5px] py-[11.5px] text-sm font-semibold text-[#ff5a1f] hover:bg-[#fff1ec] hover:text-[#ff5a1f]"
          >
            Review Queue <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        )}
      </OrgCard>

      {/* KPI ROW */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <KpiCard
          label="Placement Ready Rate"
          value={`${Math.round(ns.ready_pct)}%`}
          caption={`${ns.ready_count} of ${ns.active_count} students`}
          accent="orange"
          progressPct={ns.ready_pct}
        />
        <KpiCard
          label="Weekly Active"
          value={`${Math.round(ins.weekly_active.pct)}%`}
          caption={`${ins.weekly_active.active_count ?? 0} of ${ins.weekly_active.total_count ?? 0} students`}
          accent="teal"
          progressPct={ins.weekly_active.pct}
          delta={
            trendsData && trendsData.weekly_active.series.length >= 2
              ? trendsData.weekly_active.delta_pct
              : undefined
          }
          series={trendsData?.weekly_active.series.map((p) => p.value)}
        />
        <KpiCard
          label="Coverage"
          value={`${Math.round(ins.coverage.pct)}%`}
          caption={`${ins.coverage.covered ?? 0} of ${ins.coverage.total ?? 0} assessed`}
          accent="blue"
          progressPct={ins.coverage.pct}
          delta={
            trendsData && trendsData.coverage.series.length >= 2
              ? trendsData.coverage.delta_pct
              : undefined
          }
          series={trendsData?.coverage.series.map((p) => p.value)}
        />
      </div>

      {/* BATCH PERFORMANCE */}
      <OrgCard className="overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5">
          <h2 className="text-lg font-semibold text-[#111827]">Batch Performance</h2>
          <Link
            to="/org/batches"
            className="flex items-center gap-1 text-sm font-medium text-[#ff5a1f] hover:underline"
          >
            View all batches <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div
          className="grid items-center gap-4 border-t border-[#ececec] px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.55px] text-[#9ca3af]"
          style={{ gridTemplateColumns: 'minmax(160px,2fr) 90px 150px 150px 90px 130px' }}
        >
          <span>Batch</span>
          <span>Students</span>
          <span>Placement Ready</span>
          <span>Weekly Active</span>
          <span>Trend</span>
          <span className="text-right">Action</span>
        </div>
        {ins.batches.map((b) => {
          const batchTrend = trendsData?.batches.find((t) => t.batch_id === b.batch_id);
          const series = batchTrend?.series.map((p) => p.value);
          const dotColor = b.weekly_active_pct >= 70 ? '#0f766e' : '#ff5a1f';
          return (
            <div
              key={b.batch_id}
              className="grid items-center gap-4 border-t border-[#ececec] px-6 py-[19px]"
              style={{ gridTemplateColumns: 'minmax(160px,2fr) 90px 150px 150px 90px 130px' }}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: dotColor }}
                />
                <span className="truncate text-sm font-semibold text-[#111827]">{b.name}</span>
              </div>
              <span className="text-sm text-[#374151]">{b.student_count}</span>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-[#111827]">
                  {Math.round(b.avg_readiness)}%
                </span>
                <OrgProgressBar
                  pct={b.avg_readiness}
                  barClassName="bg-[#0f766e]"
                  className="w-24"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-[#111827]">
                  {Math.round(b.weekly_active_pct)}%
                </span>
                <OrgProgressBar
                  pct={b.weekly_active_pct}
                  barClassName="bg-[#0f766e]"
                  className="w-24"
                />
              </div>
              <div>
                {series && series.length >= 2 && <Sparkline values={series} stroke="#ff5a1f" />}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() =>
                    navigate({ to: '/org/batches/$batchId', params: { batchId: b.batch_id } })
                  }
                  className="h-auto rounded-xl border-[1.5px] border-[#ff5a1f] bg-transparent px-[17.5px] py-[7.5px] text-sm font-medium text-[#ff5a1f] hover:bg-[#fff1ec] hover:text-[#ff5a1f]"
                >
                  View Batch
                </Button>
              </div>
            </div>
          );
        })}
      </OrgCard>

      {/* PRIMARY ROW: company-tier + skill-gap */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Company-tier readiness */}
        <OrgCard>
          <div className="p-5 pb-2">
            <h3 className="font-inter text-[18px] font-semibold text-[#111827]">
              Company-tier readiness
            </h3>
            <p className="mt-1 text-xs text-[#6b7280]">
              Who you can put forward, and for which drive
            </p>
          </div>
          <div className="p-5 pt-2">
            <div className="flex h-3 w-full overflow-hidden rounded-full">
              {BAND_ORDER.map((b) => {
                const c = tier[b as keyof typeof tier];
                return c > 0 ? (
                  <div
                    key={b}
                    className={BAND_META[b].dotClass}
                    style={{ width: `${(c / tierTotal) * 100}%` }}
                    title={`${BAND_META[b].label}: ${c}`}
                  />
                ) : null;
              })}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {BAND_ORDER.map((b) => (
                <Link
                  key={b}
                  to="/org/readiness"
                  className="flex items-center gap-2 rounded-lg border border-[#ececec] px-3 py-2 hover:bg-[#f9fafb]"
                >
                  <span className={cn('h-2.5 w-2.5 rounded-full', BAND_META[b].dotClass)} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold leading-none text-[#111827]">
                      {tier[b as keyof typeof tier]}
                    </div>
                    <div className="mt-0.5 text-[11px] text-[#9ca3af]">{TIER_SUBTITLE[b]}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </OrgCard>

        {/* Skill-gap heatmap */}
        <OrgCard>
          <div className="p-5 pb-2">
            <h3 className="font-inter text-[18px] font-semibold text-[#111827]">
              Cohort skill-gap heatmap
            </h3>
            <p className="mt-1 text-xs text-[#6b7280]">
              Where your cohort is weakest — the skills to prioritise (weakest first)
            </p>
          </div>
          <div className="p-5 pt-2">
            {ins.skill_gap.length === 0 ? (
              <p className="py-6 text-center text-sm text-[#9ca3af]">No skill data yet.</p>
            ) : (
              <div className="space-y-1.5">
                <div
                  className="grid items-center gap-1.5 text-[10px] font-semibold uppercase text-[#9ca3af]"
                  style={{ gridTemplateColumns: '120px repeat(4,1fr)' }}
                >
                  <span />
                  {GAP_COLS.map((g) => (
                    <span key={g.key} className="text-center">
                      {g.label}
                    </span>
                  ))}
                </div>
                {ins.skill_gap.slice(0, 8).map((row) => (
                  <div
                    key={row.skill}
                    className="grid items-center gap-1.5"
                    style={{ gridTemplateColumns: '120px repeat(4,1fr)' }}
                  >
                    <span className="truncate text-xs text-[#6b7280]" title={row.skill}>
                      {row.skill}
                    </span>
                    {GAP_COLS.map((g) => {
                      const v = row[g.key] as number;
                      if (v <= 0) {
                        return (
                          <div
                            key={g.key}
                            className="flex h-7 items-center justify-center rounded bg-[#f3f4f6] text-[11px] font-bold text-[#d1d5db]"
                          />
                        );
                      }
                      return (
                        <Link
                          key={g.key}
                          to="/org/students"
                          search={{ skill: row.skill, skillBand: g.key }}
                          className={cn(
                            'flex h-7 items-center justify-center rounded text-[11px] font-bold text-white transition-opacity hover:opacity-80',
                            g.cls,
                          )}
                          title={`View ${v} ${g.label.toLowerCase()} student${v === 1 ? '' : 's'} in ${row.skill}`}
                        >
                          {v}
                        </Link>
                      );
                    })}
                  </div>
                ))}
                {(() => {
                  const assessed = Math.max(0, ...ins.skill_gap.map((r) => r.total));
                  return (
                    <p className="pt-1.5 text-[11px] text-[#9ca3af]">
                      Based on {assessed} student{assessed === 1 ? '' : 's'} with graded assessments
                      · counts show how many fall in each band per skill
                    </p>
                  );
                })()}
              </div>
            )}
          </div>
        </OrgCard>
      </div>
    </div>
  );
}
